/**
 * EducationScholarshipsList — /education/scholarships
 */
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Award, PlusCircle, Calendar, Loader2 } from "lucide-react";
import { http } from "@/lib/api";
import EducationCategoryNav from "@/components/EducationCategoryNav";

export default function EducationScholarshipsList() {
  const [params, setParams] = useSearchParams();
  const [meta, setMeta] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const setFilter = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next);
  };

  useEffect(() => { http.get("/education/meta").then(r => setMeta(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    setLoading(true);
    const p = Object.fromEntries([...params.entries()]);
    http.get("/education/scholarships", { params: p })
      .then(r => { setItems(r.data.items || []); setTotal(r.data.total || 0); })
      .catch(() => { setItems([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [params]);

  const category   = params.get("category") || "";
  const level      = params.get("level") || "";
  const country    = params.get("country") || "";
  const open_only  = params.get("open_only") === "true";
  const q          = params.get("q") || "";

  return (
    <>
      <EducationCategoryNav />
      <div className="max-w-6xl mx-auto px-4 py-8" data-testid="scholarships-list-page">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
          <div>
            <div className="overline mb-1">SCHOLARSHIPS</div>
            <h1 className="font-display text-4xl mb-1">Scholarships &amp; financial aid</h1>
            <p className="opacity-70">{total} listed</p>
          </div>
          <Link to="/education/scholarships/new" className="wofy-btn-primary inline-flex items-center gap-1.5" data-testid="cta-new-scholarship">
            <PlusCircle className="w-4 h-4" /> Post scholarship
          </Link>
        </div>

        <div className="wofy-card !p-3 mb-6 flex flex-wrap gap-2 items-center">
          <select value={category} onChange={e => setFilter("category", e.target.value)}
            className="text-sm px-2 py-1 rounded border" style={{ borderColor: "var(--wofy-border)" }}
            data-testid="filter-category">
            <option value="">All categories</option>
            {meta?.scholarship_categories?.map(c => <option key={c.slug} value={c.slug}>{c.label}</option>)}
          </select>
          <select value={level} onChange={e => setFilter("level", e.target.value)}
            className="text-sm px-2 py-1 rounded border" style={{ borderColor: "var(--wofy-border)" }}
            data-testid="filter-level">
            <option value="">Any level</option>
            {meta?.institution_levels?.map(l => <option key={l.slug} value={l.slug}>{l.label}</option>)}
          </select>
          <input value={country} onChange={e => setFilter("country", e.target.value)} placeholder="Country"
            className="text-sm px-2 py-1 rounded border w-32" style={{ borderColor: "var(--wofy-border)" }} />
          <label className="text-xs inline-flex items-center gap-1 opacity-80">
            <input type="checkbox" checked={open_only} onChange={e => setFilter("open_only", e.target.checked ? "true" : "")} data-testid="filter-open-only" />
            Open only
          </label>
          <input value={q} onChange={e => setFilter("q", e.target.value)} placeholder="Search…"
            className="text-sm px-2 py-1 rounded border flex-1 min-w-[8rem]" style={{ borderColor: "var(--wofy-border)" }} />
        </div>

        {loading ? (
          <div className="opacity-60 text-sm inline-flex items-center gap-1.5"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>
        ) : items.length === 0 ? (
          <div className="wofy-card !p-8 text-center">
            <p className="opacity-70 mb-3">No scholarships match. Be the first to post one.</p>
            <Link to="/education/scholarships/new" className="wofy-btn-primary inline-flex items-center gap-1.5">
              <PlusCircle className="w-4 h-4" /> Post scholarship
            </Link>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {items.map(s => (
              <Link key={s.id} to={`/education/scholarships/${s.slug}`}
                className="wofy-card !p-4 hover:shadow-md transition block"
                data-testid={`scholarship-card-${s.slug}`}>
                {s.cover_image_url && (
                  <div className="w-full h-28 rounded-lg overflow-hidden mb-3 bg-black/5"
                       style={{ backgroundImage: `url(${s.cover_image_url})`, backgroundSize: "cover", backgroundPosition: "center" }} />
                )}
                <div className="text-[10px] font-mono uppercase tracking-wider opacity-60 mb-1">
                  <Award className="w-3 h-3 inline mr-1" />{s.category}
                </div>
                <div className="font-medium mb-1 line-clamp-2">{s.name}</div>
                <div className="text-xs opacity-70">{s.provider}</div>
                {s.application_deadline && (
                  <div className="text-xs opacity-70 mt-1 inline-flex items-center gap-1">
                    <Calendar className="w-3 h-3" />Deadline: {s.application_deadline}
                  </div>
                )}
                {s.amount_summary && <div className="text-xs opacity-70 mt-1">Amount: {s.amount_summary}</div>}
              </Link>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
